import moment from 'moment-timezone';

import {
  DISCOVERY_CARD_DATE_ONLY_OPTS,
  DISCOVERY_CARD_DATE_TIME_OPTS,
} from './discoveryCardDates';

export const EVENT_DATES_QUERY_PARAM = 'eventDates';

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Calendar days for search filters follow the user's local timezone (same as PromoTix
 * `search_start_date` / discovery). Sharetribe `eventDateUnix` on native listings is
 * stored as UTC start-of-day for the wizard date string.
 */
export const getSearchCalendarTimeZone = () => {
  if (typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) {
      return tz;
    }
  }
  return 'America/New_York';
};

/**
 * Parse URL value "YYYY-MM-DD,YYYY-MM-DD" into calendar days (no time component).
 * @returns {{ startDay: string, endDay: string } | null}
 */
export const parseEventDatesParam = value => {
  if (value == null || String(value).trim() === '') {
    return null;
  }
  const parts = String(value).split(',');
  if (parts.length !== 2) {
    return null;
  }
  const startDay = parts[0].trim();
  const endDay = parts[1].trim();
  if (!ISO_DAY.test(startDay) || !ISO_DAY.test(endDay) || startDay > endDay) {
    return null;
  }
  return { startDay, endDay };
};

/**
 * @param {Date} startDate
 * @param {Date} endDate
 * @returns {string | null} "YYYY-MM-DD,YYYY-MM-DD"
 */
export const formatEventDatesParam = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return null;
  }
  const tz = getSearchCalendarTimeZone();
  const startDay = moment(startDate).tz(tz).format('YYYY-MM-DD');
  const endDay = moment(endDate).tz(tz).format('YYYY-MM-DD');
  if (!ISO_DAY.test(startDay) || !ISO_DAY.test(endDay) || startDay > endDay) {
    return null;
  }
  return `${startDay},${endDay}`;
};

/**
 * Validates event date range for URL / filter state.
 * @returns {string | null} normalized param value
 */
export const validateEventDatesParam = paramValue => {
  const range = parseEventDatesParam(paramValue);
  if (!range) {
    return null;
  }
  return `${range.startDay},${range.endDay}`;
};

/**
 * Sharetribe `pub_eventDateUnix`: value >= START and value < END (END exclusive).
 * Native listing wizard saves `eventDateUnix` as UTC start-of-day for the YYYY-MM-DD string.
 */
export const eventDatesParamToPubEventDateUnix = paramValue => {
  const range = parseEventDatesParam(paramValue);
  if (!range) {
    return {};
  }
  const startUnix = moment.utc(range.startDay, 'YYYY-MM-DD', true).startOf('day').unix();
  const endExclusiveUnix = moment
    .utc(range.endDay, 'YYYY-MM-DD', true)
    .add(1, 'day')
    .startOf('day')
    .unix();
  return { pub_eventDateUnix: `${startUnix},${endExclusiveUnix}` };
};

export const startOfUpcomingSearchDay = (now = new Date()) => {
  const tz = getSearchCalendarTimeZone();
  return moment(now).tz(tz).format('YYYY-MM-DD');
};

export const clampEventDatesParamToUpcoming = (paramValue, now = new Date()) => {
  const range = parseEventDatesParam(paramValue);
  if (!range) {
    return null;
  }
  const today = startOfUpcomingSearchDay(now);
  const startDay = range.startDay < today ? today : range.startDay;
  if (startDay > range.endDay) {
    return null;
  }
  return `${startDay},${range.endDay}`;
};

/**
 * Always constrain Marketplace search to upcoming (or clamped) event days so
 * location search cannot surface years-old listings as "soonest".
 */
export const pubEventDateUnixForSearch = (eventDatesParam, now = new Date()) => {
  if (eventDatesParam != null && String(eventDatesParam).trim() !== '') {
    const clamped = clampEventDatesParamToUpcoming(eventDatesParam, now);
    if (!clamped) {
      return { pub_eventDateUnix: '0,0' };
    }
    return eventDatesParamToPubEventDateUnix(clamped);
  }
  const startUnix = moment
    .utc(startOfUpcomingSearchDay(now), 'YYYY-MM-DD', true)
    .startOf('day')
    .unix();
  return { pub_eventDateUnix: `${startUnix},` };
};

/**
 * True when two inclusive calendar-day ranges share at least one day (string YYYY-MM-DD compare).
 */
export const calendarDayRangesOverlap = (aStartDay, aEndDay, bStartDay, bEndDay) => {
  return aStartDay <= bEndDay && aEndDay >= bStartDay;
};

const unixFromEventInstantRaw = (raw, timezoneId) => {
  if (raw == null || String(raw).trim() === '') {
    return null;
  }
  if (ISO_DAY.test(String(raw).trim())) {
    const tz = timezoneId || getSearchCalendarTimeZone();
    return moment.tz(String(raw).trim(), 'YYYY-MM-DD', true, tz).startOf('day').unix();
  }
  const n = Number(raw);
  if (!Number.isFinite(n)) {
    const t = Date.parse(String(raw));
    return Number.isFinite(t) ? Math.floor(t / 1000) : null;
  }
  return n < 1e12 ? Math.floor(n) : Math.floor(n / 1000);
};

const recurringPrimaryNextOccurrenceUnixSeconds = event => {
  if (!event || typeof event !== 'object') {
    return null;
  }
  const recurringFlag = Number(event.is_recurring_event ?? event.isRecurringEvent ?? 0);
  const isRecurring = Number.isFinite(recurringFlag) && recurringFlag > 0;
  if (!isRecurring) {
    return null;
  }

  const futureLikeCandidates = [
    event.next_occurrence_timestamp,
    event.nextOccurrenceTimestamp,
    event.next_event_timestamp,
    event.nextEventTimestamp,
    event.next_timestamp,
    event.nextTimestamp,
    event.upcoming_timestamp,
    event.upcomingTimestamp,
    event.next_occurrence_date,
    event.nextOccurrenceDate,
    event.next_event_date,
    event.nextEventDate,
    event.upcoming_date,
    event.upcomingDate,
    event.event_start,
    event.start_date,
    event.startDate,
    event.min_timestamp,
    event.minTimestamp,
  ];

  const nowSec = Math.floor(Date.now() / 1000);
  for (const raw of futureLikeCandidates) {
    const unix = unixFromEventInstantRaw(raw, event.timezone_id);
    if (unix != null && Number.isFinite(unix) && unix >= nowSec) {
      return unix;
    }
  }
  return null;
};

/**
 * Event instant for a PromoTix primary catalog row (seconds) — uses span start when available.
 * Avoids `event_date` human strings (e.g. "May 31-Jun 1,2026 10:00 PM") which `Date.parse`
 * misreads as year 2001.
 */
export const primaryEventUnixSeconds = event => {
  if (!event || typeof event !== 'object') {
    return null;
  }

  const recurringNext = recurringPrimaryNextOccurrenceUnixSeconds(event);
  if (recurringNext != null) {
    return recurringNext;
  }

  const minD = event.min_date;
  const maxD = event.max_date;
  if (minD != null && maxD != null) {
    const startDay = String(minD).trim();
    const endDay = String(maxD).trim();
    if (ISO_DAY.test(startDay) && ISO_DAY.test(endDay) && startDay <= endDay) {
      const tz = event.timezone_id || getSearchCalendarTimeZone();
      return moment.tz(startDay, 'YYYY-MM-DD', true, tz).startOf('day').unix();
    }
  }

  const minTs = event.min_timestamp ?? event.minTimestamp;
  if (minTs != null) {
    const n = Number(minTs);
    if (Number.isFinite(n)) {
      return n < 1e12 ? Math.floor(n) : Math.floor(n / 1000);
    }
  }

  return unixFromEventInstantRaw(
    event.event_date_unix ??
      event.eventDateUnix ??
      event.event_start ??
      event.start_date ??
      event.startDate ??
      null,
    event.timezone_id
  );
};

/**
 * Formats a `YYYY-MM-DD` calendar day for discovery cards.
 */
export const formatDiscoveryCalendarDay = (intl, dayStr) => {
  if (intl == null || dayStr == null) {
    return null;
  }
  const s = String(dayStr).trim();
  if (!ISO_DAY.test(s)) {
    return null;
  }
  const m = moment(s, 'YYYY-MM-DD', true);
  if (!m.isValid()) {
    return null;
  }
  return intl.formatDate(m.toDate(), DISCOVERY_CARD_DATE_ONLY_OPTS);
};

/**
 * Date line for PromoTix primary event cards (range, single day, or showtime).
 */
export const primaryEventCardDateLine = (intl, event) => {
  if (!intl || !event || typeof event !== 'object') {
    return null;
  }

  const span = primaryEventSpanDays(event);
  if (span) {
    const startLabel = formatDiscoveryCalendarDay(intl, span.startDay);
    if (!startLabel) {
      return null;
    }
    if (span.startDay !== span.endDay) {
      const endLabel = formatDiscoveryCalendarDay(intl, span.endDay);
      return endLabel ? `${startLabel} – ${endLabel}` : startLabel;
    }
    const minTs = event.min_timestamp ?? event.minTimestamp;
    if (minTs != null) {
      const n = Number(minTs);
      if (Number.isFinite(n)) {
        const tz = event.timezone_id || getSearchCalendarTimeZone();
        const m = moment.unix(n < 1e12 ? Math.floor(n) : Math.floor(n / 1000)).tz(tz);
        if (m.isValid()) {
          return intl.formatDate(m.toDate(), DISCOVERY_CARD_DATE_TIME_OPTS);
        }
      }
    }
    return startLabel;
  }

  const unix = primaryEventUnixSeconds(event);
  if (unix != null && Number.isFinite(unix)) {
    const tz = event.timezone_id || getSearchCalendarTimeZone();
    const m = moment.unix(unix).tz(tz);
    if (m.isValid()) {
      return intl.formatDate(m.toDate(), DISCOVERY_CARD_DATE_TIME_OPTS);
    }
  }

  return null;
};

/**
 * Inclusive calendar-day span for a PromoTix primary row (`min_date` / `max_date` from API).
 * @returns {{ startDay: string, endDay: string } | null}
 */
export const primaryEventSpanDays = event => {
  if (!event || typeof event !== 'object') {
    return null;
  }

  const minD = event.min_date;
  const maxD = event.max_date;
  if (minD != null && maxD != null) {
    const startDay = String(minD).trim();
    const endDay = String(maxD).trim();
    if (ISO_DAY.test(startDay) && ISO_DAY.test(endDay) && startDay <= endDay) {
      return { startDay, endDay };
    }
  }

  const minTs = event.min_timestamp ?? event.minTimestamp;
  const maxTs = event.max_timestamp ?? event.maxTimestamp;
  if (minTs != null && maxTs != null) {
    const minN = Number(minTs);
    const maxN = Number(maxTs);
    if (Number.isFinite(minN) && Number.isFinite(maxN)) {
      const tz = event.timezone_id || getSearchCalendarTimeZone();
      const startDay = moment.unix(minN).tz(tz).format('YYYY-MM-DD');
      const endDay = moment.unix(maxN).tz(tz).format('YYYY-MM-DD');
      if (startDay <= endDay) {
        return { startDay, endDay };
      }
    }
  }

  const unix = unixFromEventInstantRaw(
    event.event_date_unix ??
      event.eventDateUnix ??
      event.event_start ??
      event.start_date ??
      event.startDate ??
      null,
    event.timezone_id
  );
  if (unix == null) {
    return null;
  }
  const tz = event.timezone_id || getSearchCalendarTimeZone();
  const day = moment.unix(unix).tz(tz).format('YYYY-MM-DD');
  return { startDay: day, endDay: day };
};

/**
 * Last inclusive calendar day for a primary row (for “has this event ended?” checks).
 */
export const primaryEventEndCalendarDay = event => {
  const span = primaryEventSpanDays(event);
  return span ? span.endDay : null;
};

/**
 * True when the event’s last showtime is in the past (uses `max_timestamp`, else last calendar day).
 */
export const primaryEventHasEnded = (event, options = {}) => {
  if (!event || typeof event !== 'object') {
    return false;
  }
  const nowSec = options.nowSec ?? Math.floor(Date.now() / 1000);
  const maxTs = event.max_timestamp ?? event.maxTimestamp;
  if (maxTs != null) {
    const n = Number(maxTs);
    if (Number.isFinite(n)) {
      const sec = n < 1e12 ? Math.floor(n) : Math.floor(n / 1000);
      return sec < nowSec;
    }
  }
  const endDay = primaryEventEndCalendarDay(event);
  if (endDay) {
    const tz = event.timezone_id || getSearchCalendarTimeZone();
    const today =
      options.todayDay ?? moment().tz(tz).format('YYYY-MM-DD');
    return endDay < today;
  }
  return false;
};

/**
 * Primary catalog rows match when their event span overlaps the filter (handles recurring /
 * multi-day events via min_date–max_date, and single showtimes via fallback).
 */
export const primaryEventMatchesEventDatesFilter = (event, eventDatesParam) => {
  const range = parseEventDatesParam(eventDatesParam);
  if (!range) {
    return true;
  }
  const span = primaryEventSpanDays(event);
  if (!span) {
    return false;
  }
  return calendarDayRangesOverlap(
    span.startDay,
    span.endDay,
    range.startDay,
    range.endDay
  );
};
